// frontend/src/BesinYonetimi.js
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Modal, Form, Row, Col, Button, Spinner } from 'react-bootstrap';
import api from './api';

function BesinYonetimi() {
  const [besinler, setBesinler] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [duzenlenenBesin, setDuzenlenenBesin] = useState(null);
  const [hazirYukleniyor, setHazirYukleniyor] = useState(false);
  const [arama, setArama] = useState('');

  const besinleriGetir = useCallback(async () => {
    try {
      const response = await api.get('/diyetisyen/besinler');
      setBesinler(response.data);
    } catch (error) { console.error("Besinler getirilemedi:", error); }
  }, []);

  useEffect(() => { besinleriGetir(); }, [besinleriGetir]);

  const filtrelenmis = useMemo(() => {
    const q = arama.trim().toLocaleLowerCase('tr');
    if (!q) return besinler;
    return besinler.filter(b => b.ad?.toLocaleLowerCase('tr').includes(q));
  }, [besinler, arama]);

  const handleSil = async (besinId) => {
    if (!window.confirm('Bu besini silmek istediğinizden emin misiniz?')) return;
    try {
      await api.delete(`/diyetisyen/besinler/${besinId}`);
      besinleriGetir();
    } catch (error) { alert(error.response?.data?.mesaj || 'Silme işlemi başarısız'); }
  };

  const handleHazirKutuphaneYukle = async () => {
    if (!window.confirm('Yaygın besinlerden oluşan hazır kütüphane (~85 besin, makro değerleriyle) listenize eklenecek. Zaten mevcut olan besinler atlanır. Devam edilsin mi?')) return;
    setHazirYukleniyor(true);
    try {
      const res = await api.post('/diyetisyen/besinler/hazir-yukle');
      alert(res.data.mesaj);
      besinleriGetir();
    } catch (error) {
      alert(error.response?.data?.mesaj || 'Hazır kütüphane yüklenemedi.');
    } finally {
      setHazirYukleniyor(false);
    }
  };

  const handleModalOpen = (besin = null) => {
    setDuzenlenenBesin(besin);
    setShowModal(true);
  };

  const handleFormSubmit = async (e) => {
    e.preventDefault();
    const formData = {
        ad: e.target.elements.ad.value,
        birim: e.target.elements.birim.value,
        protein: e.target.elements.protein.value || null,
        karbonhidrat: e.target.elements.karbonhidrat.value || null,
        yag: e.target.elements.yag.value || null,
    };

    const isUpdating = !!duzenlenenBesin?.id;
    const url = isUpdating ? `/diyetisyen/besinler/${duzenlenenBesin.id}` : '/diyetisyen/besinler';
    const method = isUpdating ? 'put' : 'post';
    try {
      await api[method](url, formData);
      setShowModal(false);
      besinleriGetir();
    } catch (error) {
      alert(`Hata: ${error.response?.data?.mesaj || 'İşlem başarısız'}`);
    }
  };

  return (
    <div>
      {/* Baslik + aksiyonlar */}
      <div className="page-toolbar">
        <div>
          <h1 style={{ fontSize: '1.75rem' }}>Besin Kütüphanesi</h1>
          <p className="subtitle">
            {besinler.length} besin • protein, karbonhidrat ve yağ değerleriyle
          </p>
        </div>
        <div className="toolbar-actions">
          <div className="ui-search">
            <span className="material-symbols-outlined">search</span>
            <input
              type="text"
              placeholder="Besin ara..."
              value={arama}
              onChange={(e) => setArama(e.target.value)}
            />
          </div>
          <button className="btn-modern ghost" disabled={hazirYukleniyor} onClick={handleHazirKutuphaneYukle}>
            {hazirYukleniyor
              ? <Spinner size="sm" animation="border" />
              : <><span className="material-symbols-outlined" style={{ fontSize: '1.05rem' }}>library_add</span> Hazır Kütüphane</>}
          </button>
          <button className="btn-modern primary" onClick={() => handleModalOpen()}>
            <span className="material-symbols-outlined" style={{ fontSize: '1.05rem' }}>add</span> Yeni Besin
          </button>
        </div>
      </div>

      {/* Besin kartlari */}
      {filtrelenmis.length === 0 ? (
        <div className="ui-card">
          <div className="empty-state">
            <span className="material-symbols-outlined">
              {besinler.length === 0 ? 'nutrition' : 'search_off'}
            </span>
            {besinler.length === 0
              ? 'Henüz besin eklenmemiş. "Hazır Kütüphane" ile hızlı başlayabilirsiniz.'
              : 'Aramanızla eşleşen besin bulunamadı.'}
          </div>
        </div>
      ) : (
        <div className="card-grid">
          {filtrelenmis.map((besin) => {
            const p = Number(besin.protein) || 0;
            const k = Number(besin.karbonhidrat) || 0;
            const y = Number(besin.yag) || 0;
            const toplam = p + k + y;
            const yuzde = (v) => (toplam > 0 ? `${(v / toplam) * 100}%` : '0%');
            return (
              <div key={besin.id} className="ui-card hoverable food-card">
                <div className="food-card__head">
                  <div>
                    <div className="food-card__title">{besin.ad}</div>
                    <span className="food-card__unit">{besin.birim}</span>
                  </div>
                  <div className="kcal-badge">
                    {besin.kalori?.toFixed(0)}
                    <small>kcal</small>
                  </div>
                </div>

                <div className="macro-bar" title="Makro dağılımı">
                  <i className="pro" style={{ width: yuzde(p) }} />
                  <i className="carb" style={{ width: yuzde(k) }} />
                  <i className="fat" style={{ width: yuzde(y) }} />
                </div>

                <div className="macro-row">
                  <div className="macro-pill pro"><b>{p}g</b><span>Protein</span></div>
                  <div className="macro-pill carb"><b>{k}g</b><span>Karb.</span></div>
                  <div className="macro-pill fat"><b>{y}g</b><span>Yağ</span></div>
                </div>

                <div className="food-card__actions">
                  <button className="icon-btn" onClick={() => handleModalOpen(besin)}>
                    <span className="material-symbols-outlined">edit</span> Düzenle
                  </button>
                  <button className="icon-btn danger" onClick={() => handleSil(besin.id)}>
                    <span className="material-symbols-outlined">delete</span> Sil
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Ekle / Duzenle modal */}
      <Modal show={showModal} onHide={() => setShowModal(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>{duzenlenenBesin?.id ? 'Besin Düzenle' : 'Yeni Besin Ekle'}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form onSubmit={handleFormSubmit}>
            <Form.Group className="mb-3">
              <Form.Label>Besin Adı</Form.Label>
              <Form.Control name="ad" type="text" defaultValue={duzenlenenBesin?.ad || ''} required />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Değerlerin Birimi</Form.Label>
              <Form.Control name="birim" type="text" defaultValue={duzenlenenBesin?.birim || '100g'} placeholder="örn: 100g, 1 adet" required />
            </Form.Group>
            <Row>
              <Col>
                <Form.Group className="mb-3">
                  <Form.Label>Protein (g)</Form.Label>
                  <Form.Control name="protein" type="number" step="0.1" defaultValue={duzenlenenBesin?.protein || ''} />
                </Form.Group>
              </Col>
              <Col>
                <Form.Group className="mb-3">
                  <Form.Label>Karbonhidrat (g)</Form.Label>
                  <Form.Control name="karbonhidrat" type="number" step="0.1" defaultValue={duzenlenenBesin?.karbonhidrat || ''} />
                </Form.Group>
              </Col>
              <Col>
                <Form.Group className="mb-3">
                  <Form.Label>Yağ (g)</Form.Label>
                  <Form.Control name="yag" type="number" step="0.1" defaultValue={duzenlenenBesin?.yag || ''} />
                </Form.Group>
              </Col>
            </Row>
            <div className="d-flex justify-content-end mt-3">
              <Button variant="secondary" onClick={() => setShowModal(false)} className="me-2">İptal</Button>
              <Button variant="primary" type="submit">Kaydet</Button>
            </div>
          </Form>
        </Modal.Body>
      </Modal>
    </div>
  );
}
export default BesinYonetimi;
